import type { HttpClient } from './http.ts';
import type {
  ArchiveResult,
  AuthResult,
  ByShopQuery,
  ByShopResponse,
  CreateOwnerRequest,
  CreateReportRequest,
  CreateShopRequest,
  CreateShopResponse,
  CreateUserRequest,
  DashboardConfig,
  DeleteResult,
  EnrollmentCodeResponse,
  FxRatesResponse,
  ImportApplyResponse,
  ImportPreviewResponse,
  ImportRequest,
  LicenceResponse,
  LoginRequest,
  MeResponse,
  MintLicenceResponse,
  OwnerShopLinkResult,
  OwnerSummary,
  PermissionRef,
  PotdResponse,
  ReportsListQuery,
  ReportsListResponse,
  ReportImportsResponse,
  ReportsSummaryQuery,
  ReportsSummaryResponse,
  RevenueQuery,
  RevenueResult,
  RoleSummary,
  ShopStatus,
  ShopStatusUpdateResponse,
  ShopSummary,
  TimeseriesQuery,
  TimeseriesResult,
  UpdateConfigRequest,
  UpdateOwnerRequest,
  UpdateReportRequest,
  UpdateRolePermissionsResponse,
  UpdateShopBillingRequest,
  UpdateShopRequest,
  UpdateUserRequest,
  UserSummary,
} from './types.ts';

const BASE = '/v2/dashboard';

type QueryParams = Record<string, string | number | boolean | undefined>;

export function authApi(http: HttpClient) {
  return {
    login: (body: LoginRequest): Promise<AuthResult> =>
      http.post(`${BASE}/auth/login`, { body, auth: false }),
    refresh: (): Promise<AuthResult> => http.post(`${BASE}/auth/refresh`, { auth: false }),
    logout: (): Promise<{ ok: true }> => http.post(`${BASE}/auth/logout`, { auth: false }),
  };
}

export function meApi(http: HttpClient) {
  return {
    get: (): Promise<MeResponse> => http.get(`${BASE}/auth/me`),
  };
}

export function shopsApi(http: HttpClient) {
  return {
    list: (includeArchived = false): Promise<{ shops: ShopSummary[] }> =>
      http.get(`${BASE}/shops`, { query: { include_archived: includeArchived ? 'true' : undefined } }),
    get: (id: number): Promise<ShopSummary> => http.get(`${BASE}/shops/${id}`),
    create: (body: CreateShopRequest): Promise<CreateShopResponse> =>
      http.post(`${BASE}/shops`, { body }),
    update: (id: number, body: UpdateShopRequest): Promise<ShopSummary> =>
      http.put(`${BASE}/shops/${id}`, { body }),
    updateBilling: (id: number, body: UpdateShopBillingRequest): Promise<ShopSummary> =>
      http.patch(`${BASE}/shops/${id}/billing`, { body }),
    newEnrollmentCode: (id: number): Promise<EnrollmentCodeResponse> =>
      http.post(`${BASE}/shops/${id}/enrollment-code`),
    keyReset: (id: number): Promise<EnrollmentCodeResponse> =>
      http.post(`${BASE}/shops/${id}/key-reset`),
    setStatus: (id: number, status: ShopStatus): Promise<ShopStatusUpdateResponse> =>
      http.patch(`${BASE}/shops/${id}/status`, { body: { status } }),
    mintLicence: (id: number): Promise<MintLicenceResponse> =>
      http.post(`${BASE}/shops/${id}/licence`),
    getLicence: (id: number): Promise<LicenceResponse> => http.get(`${BASE}/shops/${id}/licence`),
    remove: (id: number): Promise<DeleteResult> => http.delete(`${BASE}/shops/${id}`),
    archive: (id: number): Promise<ArchiveResult> => http.post(`${BASE}/shops/${id}/archive`),
    unarchive: (id: number): Promise<ArchiveResult> => http.post(`${BASE}/shops/${id}/unarchive`),
  };
}

export function ownersApi(http: HttpClient) {
  return {
    list: (): Promise<{ owners: OwnerSummary[] }> => http.get(`${BASE}/owners`),
    get: (id: number): Promise<OwnerSummary> => http.get(`${BASE}/owners/${id}`),
    create: (body: CreateOwnerRequest): Promise<{ id: number }> => http.post(`${BASE}/owners`, { body }),
    update: (id: number, body: UpdateOwnerRequest): Promise<{ id: number }> =>
      http.put(`${BASE}/owners/${id}`, { body }),
    remove: (id: number): Promise<DeleteResult> => http.delete(`${BASE}/owners/${id}`),
    linkShop: (ownerId: number, shopId: number): Promise<OwnerShopLinkResult> =>
      http.post(`${BASE}/owners/${ownerId}/shops/${shopId}`),
    unlinkShop: (ownerId: number, shopId: number): Promise<OwnerShopLinkResult> =>
      http.delete(`${BASE}/owners/${ownerId}/shops/${shopId}`),
  };
}

export function usersApi(http: HttpClient) {
  return {
    list: (): Promise<{ users: UserSummary[] }> => http.get(`${BASE}/users`),
    create: (body: CreateUserRequest): Promise<{ id: number }> => http.post(`${BASE}/users`, { body }),
    update: (id: number, body: UpdateUserRequest): Promise<{ id: number }> =>
      http.put(`${BASE}/users/${id}`, { body }),
    /** DELETE /users/:id (E2.1 spec section 9) -- hard delete, 403 on self. */
    remove: (id: number): Promise<DeleteResult> => http.delete(`${BASE}/users/${id}`),
  };
}

export function rolesApi(http: HttpClient) {
  return {
    list: (): Promise<{ roles: RoleSummary[] }> => http.get(`${BASE}/roles`),
    listPermissions: (): Promise<{ permissions: PermissionRef[] }> =>
      http.get(`${BASE}/roles/permissions`),
    create: (name: string): Promise<{ id: number }> => http.post(`${BASE}/roles`, { body: { name } }),
    setPermissions: (id: number, permissionIds: number[]): Promise<UpdateRolePermissionsResponse> =>
      http.put(`${BASE}/roles/${id}/permissions`, { body: { permission_ids: permissionIds } }),
    remove: (id: number): Promise<DeleteResult> => http.delete(`${BASE}/roles/${id}`),
  };
}

export function configApi(http: HttpClient) {
  return {
    get: (): Promise<DashboardConfig> => http.get(`${BASE}/config`),
    update: (body: UpdateConfigRequest): Promise<DashboardConfig> => http.put(`${BASE}/config`, { body }),
  };
}

export function fxApi(http: HttpClient) {
  return {
    getRates: (): Promise<FxRatesResponse> => http.get(`${BASE}/fx/rates`),
  };
}

export function potdApi(http: HttpClient) {
  return {
    get: (): Promise<PotdResponse> => http.get(`${BASE}/potd`),
    rotate: (): Promise<PotdResponse> => http.post(`${BASE}/potd/rotate`),
  };
}

export function reportsApi(http: HttpClient) {
  return {
    list: (query: ReportsListQuery = {}): Promise<ReportsListResponse> =>
      http.get(`${BASE}/reports`, { query: query as QueryParams }),
    summary: (query: ReportsSummaryQuery): Promise<ReportsSummaryResponse> =>
      http.get(`${BASE}/reports/summary`, {
        query: query as unknown as QueryParams,
      }),
    /** GET, not a POST -- the server streams back a text/csv blob + filename. */
    csvExport: (query: ReportsSummaryQuery): Promise<{ blob: Blob; filename: string | null }> =>
      http.requestBlob('GET', `${BASE}/reports/csv-export`, {
        query: query as unknown as QueryParams,
      }),
    create: (body: CreateReportRequest): Promise<{ id: number }> => http.post(`${BASE}/reports`, { body }),
    update: (id: number, body: UpdateReportRequest): Promise<{ id: number }> =>
      http.put(`${BASE}/reports/${id}`, { body }),
    remove: (id: number): Promise<DeleteResult> => http.delete(`${BASE}/reports/${id}`),
    /** GET /reports/timeseries (E2.1 spec 5.1/11.5) -- the money-over-time
     *  chart. Returns TimeseriesGroupedResponse instead when the caller
     *  passes group_by: 'shop' (multi-shop compare mode). */
    timeseries: (query: TimeseriesQuery): Promise<TimeseriesResult> =>
      http.get(`${BASE}/reports/timeseries`, { query: query as unknown as QueryParams }),
    /** GET /reports/by-shop (E2.1 spec 5.2) -- the ranked shops table. */
    byShop: (query: ByShopQuery): Promise<ByShopResponse> =>
      http.get(`${BASE}/reports/by-shop`, { query: query as unknown as QueryParams }),
    /** GET /reports/revenue (E2.1 spec 7.5, revenue:read) -- 1NL's own
     *  commission + device licence fee take. report_type filters the
     *  population; absent = live only (server default, ROADMAP:40). */
    revenue: (query: RevenueQuery): Promise<RevenueResult> =>
      http.get(`${BASE}/reports/revenue`, { query: query as unknown as QueryParams }),
    /** v1 CSV import (SPEC-AUDITOR-CSV-IMPORT.md section 5), reports:import.
     *  Preview is a pure dry-run; apply re-parses the same body server-side. */
    importPreview: (body: ImportRequest): Promise<ImportPreviewResponse> =>
      http.post(`${BASE}/reports/import/preview`, { body }),
    importApply: (body: ImportRequest): Promise<ImportApplyResponse> =>
      http.post(`${BASE}/reports/import/apply`, { body }),
    imports: (): Promise<ReportImportsResponse> => http.get(`${BASE}/reports/imports`),
  };
}

/** The full typed API surface, assembled over one fetch core. */
export function createApi(http: HttpClient) {
  return {
    auth: authApi(http),
    me: meApi(http),
    shops: shopsApi(http),
    owners: ownersApi(http),
    users: usersApi(http),
    roles: rolesApi(http),
    config: configApi(http),
    fx: fxApi(http),
    potd: potdApi(http),
    reports: reportsApi(http),
  };
}

export type Api = ReturnType<typeof createApi>;
